import React, { useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { Card, Text, TextField, Box, Select, FormLayout, Button, Grid, BlockStack, Spinner } from '@shopify/polaris';
import { PlusIcon, DeleteIcon } from '@shopify/polaris-icons';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import { programService, validateProgramName } from '#client/services/programs/programServices';
import { useProgramActions, useProgramHeader, useProgramToast } from '#client/stores/program/useProgramStore';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { SessionContext } from '#client/contexts/SessionContext';
import { useQuery } from '@tanstack/react-query';

const ProgramHeader = () => {
    const { mode, name, originalName, companyId, companyName, programAdmins, status, notificationThreshold } = useProgramHeader();
    const { setName, setCompanyId, setCompanyName, setProgramAdmins, removeProgramAdmin, updateProgramAdmins, setNotificationThreshold } = useProgramActions();
    const { errors } = useProgramToast();
    const { addToast } = useToast();

    // customer/staff gate
    const { currentUser, isCustomerAuthenticated } = useContext(SessionContext);
    const isCustomer = isCustomerAuthenticated || currentUser?.role === 'customer';

    const [nameError, setNameError] = useState(null);

    // Validate on blur
    const handleNameBlur = async (event) => {
        const value = event?.target?.value?.trim();
        if (!value) return;

        try {
            const error = await validateProgramName(value, mode, originalName);
            setNameError(error); // or setErrors depending on what you're using
        } catch (err) {
            setNameError('Could not validate program name. Please try again.');
        }
    };

    const {
        data: companies = [],
        isLoading: isCompaniesLoading,
        isError: isCompaniesError,
        error: companiesError,
    } = useQuery({
        queryKey: ['program-companies'],
        queryFn: async () => {
            const response = await programService.fetchCompaniesForProgram();
            if (!response.success) {
                throw new Error(response.error?.message || 'Failed to fetch companies');
            }
            return response.data || [];
        },
        enabled: !isCustomer,
        staleTime: 5 * 60 * 1000,
    });

    const loading = !isCustomer && isCompaniesLoading;

    useEffect(() => {
        if (!isCompaniesError) return;
        addToast({
            type: 'error',
            message: companiesError?.message || 'Failed to fetch companies',
        });
    }, [isCompaniesError, companiesError, addToast]);

    const companyOptions = useMemo(() => {
        if (loading) {
            return [{ label: 'Loading companies...', value: '' }];
        }
        const baseOptions =
            companies?.map((company) => ({
                label: company.name,
                value: company.id.toString(),
            })) || [];
        return mode === 'create' ? [{ label: 'Select Company', value: '' }, ...baseOptions] : baseOptions;
    }, [mode, loading, companies]);

    const handleCompanyIdChange = useCallback(
        (value) => {
            if (value !== companyId?.toString()) {
                const selectedCompany = companies.find((company) => company.id.toString() === value);
                setCompanyId(value);
                setCompanyName(selectedCompany?.name || '');
            }
        },
        [companies, companyId, setCompanyId, setCompanyName]
    );

    const handleEmailChange = useCallback(
        (index, value) => {
            updateProgramAdmins(value, index);
        },
        [updateProgramAdmins]
    );

    const handleAddEmail = useCallback(() => {
        setProgramAdmins('');
    }, [setProgramAdmins]);

    const handleRemoveEmail = useCallback(
        (index) => {
            removeProgramAdmin(index);
        },
        [removeProgramAdmin]
    );

    const renderEmailField = (email, index) => {
        const emailValue = email || '';

        // Get error for this specific email field (using dot notation)
        const fieldError = errors[`programAdmins.${index}`] || errors.programAdmins;

        return (
            <BlockStack key={index} gap="200">
                <Box style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', paddingBottom: '10px' }}>
                    <Box style={{ flex: 1 }}>
                        <TextField
                            label={index === 0 ? 'Program Admin(s)' : `Email ${index + 1}`}
                            value={emailValue}
                            onChange={(value) => handleEmailChange(index, value)}
                            type="email"
                            autoComplete="off"
                            placeholder={`Enter email ${index + 1}`}
                            labelHidden={index > 0}
                            error={fieldError}
                            required={index === 0}
                            requiredIndicator={index === 0}
                        />
                    </Box>
                    {programAdmins.length > 1 && (
                        <Box style={{ marginBottom: '2px' }}>
                            <Button icon={DeleteIcon} onClick={() => handleRemoveEmail(index)} accessibilityLabel={`Remove email ${index + 1}`} />
                        </Box>
                    )}
                </Box>
            </BlockStack>
        );
    };

    const viewStatusMarkup = (
        <Box paddingBlock="400">
            <Grid>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <BlockStack gap="200">
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Program:
                            </Text>{' '}
                            <Text as="span">{loading && !name ? <Spinner size="small" /> : name || 'Untitled Program'}</Text>
                        </Text>
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Program Admin(s):
                            </Text>{' '}
                            <Text as="span">{loading ? <Spinner size="small" /> : mode === 'view' && programAdmins?.length > 0 ? programAdmins.join(', ') : 'No program admins assigned'}</Text>
                        </Text>
                        <Text variant="headingSm" as="h3">
                            Notification Threshold: {notificationThreshold !== '' && notificationThreshold !== null && notificationThreshold !== undefined ? notificationThreshold : '—'}
                        </Text>
                    </BlockStack>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <BlockStack gap="200">
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Customer:
                            </Text>{' '}
                            <Text as="span">{loading && !companyName ? <Spinner size="small" /> : companyName || 'No company selected'}</Text>
                        </Text>
                        <Text as="span">
                            <Text variant="headingSm" as="span">
                                Status:
                            </Text>{' '}
                            <Text as="span">
                                <CustomBadge status={status || 'Active'} />
                            </Text>
                        </Text>
                    </BlockStack>
                </Grid.Cell>
            </Grid>
        </Box>
    );

    const editCreateStatusMarkup = (
        <>
            <FormLayout.Group>
                <TextField label="Program Name" value={name || ''} onChange={(value) => setName(value)} onBlur={handleNameBlur} error={nameError || errors.name || ''} required requiredIndicator />
                <Select
                    label="Customer"
                    options={companyOptions}
                    onChange={handleCompanyIdChange}
                    value={companyId?.toString() || ''}
                    disabled={loading}
                    error={errors.companyId || ''}
                    required
                    requiredIndicator
                />
            </FormLayout.Group>

            <Box paddingBlockStart="400">
                <Grid>
                    <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                        <BlockStack gap="200">
                            {(programAdmins && programAdmins.length > 0 ? programAdmins : ['']).map((email, index) => renderEmailField(email, index))}

                            {/* Button stays with admin list */}
                            <Box paddingBlockStart="200">
                                <Button onClick={handleAddEmail} icon={PlusIcon}>
                                    Add another email
                                </Button>
                            </Box>
                        </BlockStack>
                    </Grid.Cell>

                    <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                        <BlockStack gap="200">
                            <TextField
                                label="Notification Threshold"
                                type="number"
                                min={0}
                                step={1}
                                autoComplete="off"
                                value={notificationThreshold ?? ''}
                                onChange={(val) => setNotificationThreshold(val)}
                                error={errors.notificationThreshold || ''}
                                helpText="Applies only to Bulk Voucher creation. Sends an email when total issued vouchers reach this number."
                            />
                        </BlockStack>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }} />
                </Grid>
            </Box>
        </>
    );

    return (
        <Card>
            <Box paddingBlock="400">
                <Text variant="headingLg" as="h2">
                    Program Header
                </Text>
            </Box>
            {mode === 'view' ? viewStatusMarkup : editCreateStatusMarkup}
        </Card>
    );
};

export default ProgramHeader;
